import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { masterKeys, cronKeys, approvalKeys, graphKeys, planKeys, researchKeys, backgroundChatKeys, chatStatusKeys, chatStatusQueryConcerns, chatKeys, questionKeys, saveProposalKeys, workBoardKeys, workFileKeys, inboxKeys, workTaskKeys, itemReviewKeys, codingKeys, operatorKeys, filesOverviewKeys, attentionKeys } from '~/lib/query-keys';
import { documentKeys } from '~/hooks/queries/use-documents';
import { fileKeys } from '~/hooks/queries/use-files';
import { notificationKeys } from '~/hooks/queries/use-notifications';
import { feedKeys } from '~/hooks/queries/use-feed';
import { useSessionStore } from '~/lib/stores/session-store';
import { useEventStore, type SSEvent } from '~/features/events/event-store';
import { tryRefreshToken, signalSessionLost } from '~/lib/api-client';
import { applyArtifactUpdate } from '~/features/canvas/artifact-updated';
import { ARTIFACT_WORKSPACE_FLAG, useFeatureFlag } from '~/hooks/queries/use-feature-flags';
import { consumeLocalHistoryWrite } from '~/hooks/local-history-writer';
import { parseLiveLateResultSignal, publishLiveLateResult } from '~/features/voice/live-late-result-signal';
import { subscribeSharedStream, type OpenStream } from './shared-event-stream';
import { chatTaskKeys } from '~/lib/query-keys';
import { applyTaskChangedEvent, applyTaskProgressFrame, type ChatTask, type ChatTaskProgress } from '~/features/chat/chat-tasks-view';

/**
 * SSE listener for app-level events (cron results, approvals, heartbeats).
 *
 * Chat streaming is now handled by the AI SDK's `useChat` hook via the
 * `/api/v2/chat/:agentId/stream` proxy. This hook only subscribes to
 * non-chat events from the event bus.
 *
 * **Contract.** Every incoming event flows through two sinks:
 * 1. `pushEvent(...)` into the event-store ring buffer (audit / inspector;
 *    not a data layer — see `event-store.ts`).
 * 2. `queryClient.invalidateQueries(...)` for the affected resources, so
 *    the matching `useXxx` hooks refetch off the server. THIS is how
 *    data flows to components — never read business state out of the
 *    event payload.
 *
 * Adding a new event type? Two steps: (a) extend `SSE_EVENT_TYPES` so
 * it's subscribed to, (b) add a `case` that invalidates the matching
 * query keys. Use the typed key factories in `lib/query-keys.ts`; don't
 * pass raw string arrays.
 */
export const SSE_EVENT_TYPES = [
  'heartbeat',
  'cron-result',
  'approval-request',
  'approval-decided',
  'notification-changed',
  'system',
  'error',
  'activity-update',
  'graph-changed',
  'memory-doc-changed',
  'file-changed',
  'work-files-changed',
  'work-tasks-changed',
  'inbox-changed',
  'plan-changed',
  'questions-changed',
  'save-proposal-changed',
  'skill-progress',
  'research-changed',
  'background-chat-changed',
  'chat-status-changed',
  'chat-title-changed',
  'chat-response',
  'chat-scope-changed',
  'operator-run-updated',
  'artifact-updated',
  'voice-live-late-result',
  'chat-task-changed',
  'chat-task-progress',
] as const;

/**
 * FRONT-1113: fold one pushed approval event into the cached pending-approval
 * list. Pure and named rather than inline at the call site — this is the
 * reconciliation the card actually renders from, so it should be readable on
 * its own. Adding is idempotent (the same event can arrive twice); an
 * unrecognized/incomplete event leaves the cache untouched.
 */
function applyApprovalEvent(
  old: unknown,
  type: 'approval-request' | 'approval-decided',
  approval: { id: string } | undefined,
  decidedId: string | undefined,
): unknown {
  const list = Array.isArray(old) ? (old as Array<{ id: string }>) : [];
  if (type === 'approval-request' && approval?.id) {
    return list.some((a) => a.id === approval.id) ? list : [...list, approval];
  }
  if (type === 'approval-decided' && decidedId) {
    return list.filter((a) => a.id !== decidedId);
  }
  return old;
}

/**
 * FRONT-1739: the actual per-event-type dispatch, extracted to a plain
 * top-level function so it's directly callable from a unit test — this
 * suite runs under plain node with no jsdom (CLAUDE.md), and `useEffect`
 * (where this used to live, closed over `queryClient`/`sessionId`/
 * `pushEvent`) never runs under `renderToStaticMarkup`, so there was no
 * way to exercise the switch below without this extraction. Behavior is
 * unchanged: `useSSE`'s effect below calls this with the same three
 * closure values, now passed explicitly via `ctx`.
 */
export function handleSSEEvent(
  type: string,
  e: MessageEvent,
  ctx: {
    queryClient: QueryClient;
    sessionId: string;
    pushEvent: (event: SSEvent) => void;
    /** `eddy.artifact-workspace` is on: `artifact-updated` refreshes the open Canvas item. Absent = off. */
    artifactWorkspace?: boolean;
  },
) {
  const { queryClient, sessionId, pushEvent } = ctx;
  let payload: Record<string, unknown> = {};
  try {
    payload = JSON.parse(e.data);
  } catch {
    return;
  }
  const enriched = { ...payload, type };
  pushEvent({ type, data: enriched, timestamp: Date.now() });

  switch (type) {
    case 'heartbeat':
      queryClient.invalidateQueries({ queryKey: masterKeys.status() });
      break;
    case 'cron-result':
      queryClient.invalidateQueries({ queryKey: cronKeys.all });
      // Cron runs surface in the feed too — keep the feed in sync
      // without a separate polling timer.
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      // FRONT-3227: the tabbed Inbox's New tab reads the feed under its own key.
      queryClient.invalidateQueries({ queryKey: [...inboxKeys.all, 'new'] });
      break;
    case 'approval-request':
    case 'approval-decided': {
      // FRONT-1113: apply the pushed payload to the cache DIRECTLY rather
      // than only invalidating. A tier-3 approval is raised while a chat
      // turn is blocked on it, and that blocked request (plus the live SSE
      // streams) can exhaust the browser's per-origin HTTP/1.1 connection
      // budget — so the refetch an invalidation triggers may not complete
      // until the approval is answered. That is a deadlock: the card the
      // user must click can't load until they've clicked it. The event
      // already carries everything the card needs, so no round-trip is
      // required to show it.
      const approval = payload.approval as { id: string } | undefined;
      const decidedId = typeof payload.id === 'string' ? payload.id : undefined;
      queryClient.setQueryData(approvalKeys.list(), (old: unknown) =>
        applyApprovalEvent(old, type, approval, decidedId),
      );
      // Still reconcile with the server — it just isn't what the card
      // waits on any more.
      queryClient.invalidateQueries({ queryKey: approvalKeys.list() });
      // FRONT-3227: a pending approval is also a row in the Inbox's Review tab
      // (and its count on the Review tab).
      queryClient.invalidateQueries({ queryKey: inboxKeys.review() });
      // A pending approval drives a chat's needs-you (amber) status.
      queryClient.invalidateQueries({ queryKey: chatStatusKeys.all });
      break;
    }
    case 'notification-changed':
      // Root prefix — `notificationKeys.list()` defaults to the 'all'
      // status, which would NOT match an active `useNotifications('pending')`
      // query. The root matches every status variant plus count().
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      break;
    case 'plan-changed':
      // Plan state lives per-session; invalidate everything under the
      // 'plans' root so the active session's hook refetches.
      queryClient.invalidateQueries({ queryKey: planKeys.all });
      // FRONT-3227: a plan waiting for a go-ahead is a Review row (and the Inbox's Review count).
      queryClient.invalidateQueries({ queryKey: inboxKeys.review() });
      // FRONT-662: a plan awaiting approval drives a chat's needs-you
      // (amber) status — refresh the dot the moment the card appears.
      queryClient.invalidateQueries({ queryKey: chatStatusKeys.all });
      break;
    case 'questions-changed':
      // Prefix match — refetches every session card under the
      // `questions` root (cheap; the per-session hook is small).
      queryClient.invalidateQueries({ queryKey: questionKeys.all });
      // FRONT-3227: a pending question is a Review row (and the Inbox's Review count).
      queryClient.invalidateQueries({ queryKey: inboxKeys.review() });
      // A pending question drives a chat's needs-you (amber) status.
      queryClient.invalidateQueries({ queryKey: chatStatusKeys.all });
      // FRONT-1681: this event also fires for a coding session parking
      // on an ask_user question (createQuestions publishes on the
      // user channel regardless of which surface's `chat_questions`
      // batch it is — see questions-store.ts's `publish`) — invalidate
      // the coding-session list too, so a build's row on the chat
      // overview flips to "waiting on your answer" promptly instead of
      // waiting for the next ACTIVITY_POLL_MS tick.
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
      break;
    case 'save-proposal-changed':
      // Prefix match — refetches every session's save-proposal card
      // under the `save-proposals` root. Was previously in the event
      // bus's QUEUABLE_EVENTS list but never subscribed here, so no
      // browser ever heard it; `useSaveProposal`'s 3s poll (deliberately
      // left as-is) was doing all the work. Unlike plan-changed /
      // questions-changed, this deliberately does NOT touch
      // chatStatusKeys — chat-status-store.ts's needs-you (amber)
      // derivation never reads voice_save_proposals, only questions,
      // approvals and plans.
      queryClient.invalidateQueries({ queryKey: saveProposalKeys.all });
      break;
    case 'research-changed':
      // Deep-research status transitions (start, complete, cancel).
      // Invalidate the whole tree so both the active card and any
      // open SourcesBlock refetch.
      //
      // Deliberately does NOT touch the chat-status tree any more: since
      // FRONT-787 a research start or finish cannot change any chat's
      // status, and this event is an untargeted broadcast — so that refetch
      // was every connected user's browser re-reading a value that could
      // not have moved.
      queryClient.invalidateQueries({ queryKey: researchKeys.all });
      break;
    case 'chat-status-changed': {
      // FRONT-1681 (review round): this event also fires for a coding
      // build's own seen-write (src/coding/seen.ts's
      // markCodingSessionSeen reuses this SAME event — chat_seen backs
      // both), and its payload's `sessionId` now carries the `coding:`
      // namespace prefix (questionsSessionId, src/coding/ask-user-tool.ts)
      // specifically so this handler CAN tell the two apart, rather than
      // invalidating both trees on every seen-write regardless of which
      // one changed — every user's browser was refetching its own chat
      // statuses on ANY other user's build being opened, since the
      // publish used to be an untargeted broadcast with no
      // namespacing at all (fixed alongside this: markCodingSessionSeen
      // now also targets the writing user's own channel, not everyone).
      const csSessionId = typeof payload.sessionId === 'string' ? payload.sessionId : '';
      if (csSessionId.startsWith('coding:')) {
        // A build's seen-write — refresh only the coding-session list
        // so the row flips green→gray live on every open tab.
        queryClient.invalidateQueries({ queryKey: codingKeys.list() });
      } else {
        // FRONT-661: a chat's status changed (a seen-write, or a derived
        // transition). Refetch the status tree so the dot + indicator
        // update live across tabs/devices.
        queryClient.invalidateQueries({ queryKey: chatStatusKeys.all });
      }
      break;
    }
    case 'chat-title-changed':
      // FRONT-1804: the server just wrote a generated name for a chat (~3 s
      // after its first Send). The surfaces that show a chat's name (window
      // header, switcher, Chats page, phone) read a key nested under
      // `threadsRoot()`, so one prefix refresh repaints them; the command
      // palette's recent list (`recentsKeys`) is NOT under it and refetches
      // when the palette opens. The event
      // reaches this tab only on the replica that named the chat; otherwise
      // the window polls its own row while its turn runs (use-chats.ts) and
      // the stream-end invalidation is the last fallback.
      queryClient.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
      break;
    case 'background-chat-changed': {
      const bgSessionId = typeof payload.sessionId === 'string' ? payload.sessionId : '';
      // FRONT-1717: the payload carries the row itself (`data: { sessionId,
      // run }`, src/agents/background-chat-store.ts's `publish`), and
      // `run.status` is what tells the two ends of a turn apart.
      const run = payload.run as { status?: unknown; runId?: unknown } | null | undefined;
      const runStatus = typeof run?.status === 'string' ? run.status : undefined;
      const runId = typeof run?.runId === 'string' ? run.runId : undefined;
      // Only a POSITIVELY identified 'running' counts as a turn start.
      // `BackgroundChatStatus` is 'running' | 'completed' | 'failed' |
      // 'cancelled' and `settleBackgroundChat` is typed
      // `Exclude<BackgroundChatStatus, "running">`, so a settle can never
      // carry 'running' — but the test that matters is the direction of
      // the DEFAULT: written as `!== 'running'` instead, a payload with no
      // `run` at all (an older backend against a newer frontend) would
      // silently read as a settle at BOTH ends and quietly restore the
      // churn this fixes, looking correct all the while. Written this way
      // an unknown-shape event degrades to invalidating — the old
      // behaviour, whose cost is a wasted round trip, rather than an answer
      // that never paints (which is what FRONT-542 needs the settle
      // invalidation for). Guarded by a test.
      const isTurnStart = runStatus === 'running';

      // FRONT-204: a background chat transitioned (start, complete, fail).
      // The inspector card, the inbox dot, and the reopened-modal banner
      // update the instant the server reports it — not just on the 2s poll.
      // (FRONT-1858: targeted at the run owner's user channel; the
      // session id rides in the payload, which is all the wire carries.)
      //
      // FRONT-1717: scoped to the session the event concerns instead of the
      // whole `background-chat` tree. `bySession` is what the inbox dot,
      // the reopened-modal banner and the day-one chat read
      // (`useBackgroundChatForSession`) — one entry per session, so the
      // unscoped invalidation made every OTHER open chat's row refetch on
      // any one chat's turn. `list()` is the Inspector's all-runs list
      // (`useBackgroundChats`), which genuinely changes on any run and has
      // no per-session key to narrow to. Together those are every live
      // consumer of this tree (`byRun` is currently read by none).
      if (bgSessionId) {
        queryClient.invalidateQueries({ queryKey: backgroundChatKeys.bySession(bgSessionId) });
        queryClient.invalidateQueries({ queryKey: backgroundChatKeys.list() });
      } else {
        // No session id to scope by — fail open to the old broad key
        // rather than skip a consumer.
        queryClient.invalidateQueries({ queryKey: backgroundChatKeys.all });
      }

      // A run start/finish/fail drives the chat's working→done/failed status.
      // FRONT-1717: scoped by predicate rather than by key — chat-status
      // queries are keyed by a session SET, so there is no narrower key to
      // use. See `chatStatusQueryConcerns` for which queries it spares and
      // which one it necessarily still refetches (the dots depend on it).
      if (bgSessionId) {
        queryClient.invalidateQueries({
          predicate: (q) => chatStatusQueryConcerns(q.queryKey, bgSessionId),
        });
      } else {
        queryClient.invalidateQueries({ queryKey: chatStatusKeys.all });
      }

      // FRONT-705: a chat starting on a file/document surfaces (or
      // refreshes the timestamp of) that asset in the Inbox feed — refresh
      // it live instead of waiting for the 5-minute fallback poll. This
      // event also fires for plain web chats (tracked since FRONT-542),
      // which never appear in the feed — narrow to inbox sessions so a
      // web-chat turn starting/stopping doesn't refetch /api/feed for
      // nothing.
      if (bgSessionId.startsWith('inbox-')) {
        queryClient.invalidateQueries({ queryKey: feedKeys.all });
      }

      // When the run SETTLES while its modal is open, the answer was just
      // persisted to that session's chat — refetch its history so the
      // banner is replaced by the real reply without a manual reopen.
      //
      // FRONT-1717, two fixes here:
      //
      // (a) Settle only. This used to run on every transition, so a turn
      //     START forced a real mid-answer `/history` refetch (the query
      //     sets `staleTime: 0`, use-agent-chat.ts). In the tab that is
      //     answering, that refetch could not reseed anything —
      //     `shouldReseedFromHistory` correctly refuses while streaming —
      //     so it only cost the round trip.
      //
      //     Named consequence (code-review finding), accepted: `publish`
      //     targets the owner's user channel, so this event also reaches
      //     the user's OTHER tabs, where nothing is streaming and the
      //     reseed guard does not refuse. Such a tab no longer repaints at
      //     turn start — it waits for the settle. It is not losing a
      //     dependable live re-attach: the resume target that would drive
      //     one needs the streaming row `startChatStream` inserts
      //     (`chat-proxy.ts:3727`), which happens AFTER this event is
      //     published (`:3629`), so whether a turn-start refetch saw a
      //     resumable row at all was a race — and a race is not something
      //     to preserve in a ticket whose DoD rules out timing-based
      //     behaviour. The settle still paints the answer there.
      //
      //     NOT gated on `bgSessionId.startsWith('inbox-')` like the feed
      //     invalidation above, deliberately: a plain web chat closed and
      //     reopened mid-run depends on this refetch at turn end to paint
      //     the answer, which is exactly what FRONT-542 exists for, so
      //     narrowing to inbox sessions would regress it.
      //
      // (b) One writer. At turn end this raced `onFinish`'s own direct
      //     `setQueryData` on the same key — two writers, two fresh
      //     `history` references, two wakeups of the reconcile effect,
      //     which is the "why does it fire 2-3 times" the ticket asked.
      //     `consumeLocalHistoryWrite` hands the write to whichever of the
      //     two actually owns this run, order-independently: see
      //     `local-history-writer.ts` for why the local writer is the one
      //     that survives (FRONT-1685 already deleted a third writer from
      //     this path) and why a tab that did not run the turn still
      //     invalidates here.
      if (!isTurnStart) {
        const ownedByLocalWriter = consumeLocalHistoryWrite(runId);
        if (!ownedByLocalWriter && bgSessionId) {
          queryClient.invalidateQueries({
            queryKey: [...chatKeys.all, 'history', bgSessionId],
          });
        }
      }
      break;
    }
    case 'chat-response': {
      // Emitted by the skill runner (and the legacy agent handler)
      // AFTER a background run has persisted its assistant message and
      // saved its briefing document. The live foreground chat (AI SDK
      // stream) does NOT emit this, so normal turns are unaffected.
      // Unlike `skill-progress: completed`, this fires once the row
      // exists, so the chat refetch actually picks up the new message —
      // and the feed refetch surfaces the briefing in the inbox without
      // a reload. Partial prefix so it matches the `main` branch's
      // 4-segment history key.
      //
      // FRONT-1601: read the event's OWN session id — all five backend
      // emitters (src/agents/routes.ts, src/agents/service/restate.ts,
      // src/skills/runner.ts ×2, src/onboarding/day-one-seed.ts) put it
      // in `data.sessionId` — falling back to the closured session-store
      // id only if it's ever absent. Note only FOUR of those five also
      // publish on the user channel described below: the live agent turn
      // (service/restate.ts) deliberately does not, because it already
      // streams its response to the tab that asked for it and a second
      // delivery would only cause redundant refetches mid-stream. Reading the closure unconditionally
      // was a latent routing bug that never actually misfired in
      // production: until the change described next, a `chat-response`
      // for a session other than this tab's canonical one had no live
      // subscriber at all — the backend just queued it and it was lost,
      // never delivered here to invalidate the wrong chat's history.
      //
      // That queue-and-lose gap is now closed: the backend also
      // publishes `chat-response` on `userChannel(userId)`
      // (src/core/event-bus.ts), and this tab's one EventSource already
      // subscribes to that channel (FRONT-1113, added for pending
      // approvals) alongside its canonical session id
      // (src/agents/routes.ts's SSE route). So a background run
      // finishing on ANY of the user's chats now reaches this handler
      // live, carrying its own `sessionId` — the 3s research poll
      // backstop (research-refresh-gate.ts) and a manual reopen are no
      // longer the only ways an open modal ever hears about it.
      //
      // One consequence: when the canonical session id and the event's
      // own sessionId happen to be the SAME chat, both subscriptions
      // match and `deliver` fires twice for the identical event. That's
      // harmless here — this handler only invalidates query caches
      // below, it never appends a message to any list, so re-marking
      // the same queries stale twice is a no-op the second time.
      const responseSessionId =
        typeof payload.sessionId === 'string' ? payload.sessionId : sessionId;
      queryClient.invalidateQueries({
        queryKey: [...chatKeys.all, 'history', responseSessionId],
      });
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      queryClient.invalidateQueries({ queryKey: attentionKeys.all });
      // FRONT-2392: see use-files.ts's matching comment.
      queryClient.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
      // FRONT-3154: a server-written reply (a chat follow-up) moves its chat
      // to the top of the Chats list; refetch the lists so it shows live.
      queryClient.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
      break;
    }
    case 'skill-progress':
      // Live ticks from the skill runner. Filter to deep-research so
      // we only refetch citation counts while a research run is in
      // flight — every other skill runs through the same channel.
      if (payload.skillSlug === 'deep-research') {
        queryClient.invalidateQueries({ queryKey: researchKeys.all });
      }
      // When ANY async skill finishes, force the chat history to
      // refetch so the runner's chat_messages insert (briefing or
      // friendly failure body) shows up without a manual reload.
      // Partial prefix — chatKeys.history would expand to 4
      // segments and only match the `main` branch.
      {
        const phase = payload.phase as string | undefined;
        if (phase === 'completed' || phase === 'failed' || phase === 'cancelled') {
          queryClient.invalidateQueries({
            queryKey: [...chatKeys.all, 'history', sessionId],
          });
          // A finished skill may have saved a memory document
          // (deep-research briefings, reflect summaries, etc.) — refresh
          // the /files list so it appears without a hard reload.
          queryClient.invalidateQueries({ queryKey: documentKeys.all });
          queryClient.invalidateQueries({ queryKey: attentionKeys.all });
          // FRONT-2392: see use-files.ts's matching comment.
          queryClient.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
          // …and the inbox feed. The `chat-response` branch above
          // already invalidates feedKeys too — a grounded run on an
          // attended session (deep research included, FRONT-1500) posts
          // that event again now. This invalidation stays as a backstop
          // for the cases `chat-response` doesn't cover: a grounded run
          // whose chat delivery was suppressed (its `research_runs` row
          // failed to get created) publishes no `chat-response` at all,
          // and this `skill-progress` phase fires unconditionally on
          // every finish regardless — so it's the one place that still
          // reliably refreshes the feed for that case. Harmless
          // duplicate invalidation otherwise.
          queryClient.invalidateQueries({ queryKey: feedKeys.all });
        }
      }
      break;
    case 'chat-task-changed': {
      // FRONT-3211: a chat task moved (started, an agent settled, waiting on
      // an approval, settled, delivered). Fold it in so the chip moves at
      // once, then refetch: the list stays the truth. Keyed by the event's
      // OWN session id — it also arrives on the user channel for every chat.
      const taskSessionId = typeof payload.sessionId === 'string' ? payload.sessionId : '';
      if (!taskSessionId) break;
      const key = chatTaskKeys.list(taskSessionId);
      const fold = () => {
        queryClient.setQueryData<ChatTask[]>(key, (prev) => applyTaskChangedEvent(prev, payload, new Date().toISOString()));
        queryClient.invalidateQueries({ queryKey: key });
      };
      if (payload.delivered !== true) {
        fold();
        break;
      }
      // Delivered: the result is in the chat's stored history now. A task
      // shown directly (a stored answer, no live turn) announces itself with
      // `chat-response` and, a moment later, this event; folding "delivered"
      // at once would release a queued send before the re-read brought the
      // answer on screen, and the send would go out ABOVE it. So re-read the
      // history first, then let the chip and the composer hold move.
      void queryClient
        .refetchQueries({ queryKey: [...chatKeys.all, 'history', taskSessionId] })
        .catch(() => {
          // A failed refetch still folds: the chip and composer hold must move even if the history is stale.
        })
        .then(fold);
      break;
    }
    case 'chat-task-progress': {
      // FRONT-3211: a branched agent's live steps. Never stored server-side,
      // so the cache is their only home.
      const taskSessionId = typeof payload.sessionId === 'string' ? payload.sessionId : '';
      if (!taskSessionId) break;
      queryClient.setQueryData<ChatTaskProgress>(chatTaskKeys.progress(taskSessionId), (prev) =>
        applyTaskProgressFrame(prev, payload),
      );
      break;
    }
    case 'voice-live-late-result': {
      // FRONT-254: not server data to refetch — a cue for the running voice
      // call (see `live-late-result-signal.ts`).
      const signal = parseLiveLateResultSignal(payload);
      if (signal) publishLiveLateResult(signal);
      break;
    }
    case 'artifact-updated':
      // FRONT-2600 event, consumed by the Canvas (FRONT-2594): a matching
      // document refetches, a matching deck/sketch frame reloads. Flag off: nothing.
      applyArtifactUpdate(queryClient, payload, ctx.artifactWorkspace === true);
      break;
    case 'graph-changed':
      queryClient.invalidateQueries({ queryKey: graphKeys.all });
      // FRONT-1003: entity/relationship/attribute writes also change the
      // board's task-adjacent aggregates (people, context snippet) —
      // keep the board grid from holding stale data (60s staleTime, no
      // refetch-on-focus).
      queryClient.invalidateQueries({ queryKey: workBoardKeys.all });
      break;
    case 'memory-doc-changed':
      // Memory documents surface on the Memory page (entities /
      // activity), the /files list (merged with user uploads),
      // AND the feed (research briefings render there).
      queryClient.invalidateQueries({ queryKey: graphKeys.all });
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      queryClient.invalidateQueries({ queryKey: attentionKeys.all });
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      // FRONT-3227: the tabbed Inbox's New tab reads the feed under its own key.
      queryClient.invalidateQueries({ queryKey: [...inboxKeys.all, 'new'] });
      // FRONT-2392: see use-files.ts's matching comment.
      queryClient.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
      // FRONT-1737, corrected FRONT-1806: a document Eddy saves mid-chat
      // via `file_write` gets auto-filed onto the chat's Work container
      // (`src/work/chat-scope-attach.ts`, which calls `attachContainerFile`)
      // — that attach now has its OWN dedicated event
      // (`work-files-changed`, see that case below), so the invalidation
      // here is no longer covering for a missing signal on the attach
      // itself. It stays because `memory-doc-changed` is the ONLY signal
      // for a document's rename / trash / restore / purge changing what a
      // project's Files panel renders: the attachment row itself is
      // unchanged (a rename) or removed by a cascade (trash/purge) that
      // publishes nothing of its own, so without this invalidation an open
      // panel would keep showing a stale name or a since-detached row.
      // `workFileKeys.all` (not `.byContainer`) because this listener
      // doesn't know which container, if any, the document is attached to.
      queryClient.invalidateQueries({ queryKey: workFileKeys.all });
      queryClient.invalidateQueries({ queryKey: workBoardKeys.all });
      break;
    case 'file-changed':
      // FRONT-1739: the `user_files` twin of `memory-doc-changed` above.
      // Two triggers publish this, not one — `updateFile`
      // (src/files/store.ts) only when a plain file's CONTENT actually
      // changed, e.g. EDDY rewriting an open upload in place via
      // `file_write`, AND (FRONT-1806) `createFile` on every brand-new
      // upload. Keep this invalidation broad rather than narrowing it back
      // to "a content change": it now also covers a newly created upload
      // appearing in the Files list, and narrowing it to only the
      // content-change trigger would silently re-break that live-refresh.
      // Root prefix so it covers both the Files list (`fileKeys.list()`)
      // and the open reader's detail query (`fileKeys.detail(id)`) in one
      // invalidation. Plain uploads never appear in the inbox feed (only
      // documents and cron-run results do), so unlike memory-doc-changed
      // there is no feedKeys invalidation here.
      queryClient.invalidateQueries({ queryKey: fileKeys.all });
      queryClient.invalidateQueries({ queryKey: attentionKeys.all });
      // FRONT-2392: see use-files.ts's matching comment.
      queryClient.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
      break;
    case 'work-files-changed':
      // FRONT-1806: `attachContainerFile`/`detachContainerFile`
      // (src/work/container-files-store.ts) publish this on every genuine
      // attach/detach so an open project's Files panel (`useWorkFiles`, 60s
      // staleTime, no polling, no refetch-on-focus) live-refreshes instead
      // of sitting stale until a manual reload. `.all` rather than
      // `.byContainer(...)` for the same reason `memory-doc-changed` above
      // invalidates the whole `workFileKeys` tree: it matches that
      // existing case, it avoids depending on the event payload's shape
      // (never read business state out of the payload, per this file's own
      // contract above), and a user has at most a couple of container
      // lists cached at once.
      queryClient.invalidateQueries({ queryKey: workFileKeys.all });
      queryClient.invalidateQueries({ queryKey: workBoardKeys.all });
      // FRONT-2568: a saved new version changes its review chip too
      // ("Awaiting approval"), and the Files list asks for the same ids again,
      // so its batch read would otherwise stay cached.
      queryClient.invalidateQueries({ queryKey: itemReviewKeys.all });
      break;
    case 'work-tasks-changed':
      // FRONT-2568: the server opened, refreshed or closed a task this tab did
      // not write (a review task for a new version, closed by a decision).
      // `.all` for the same payload-free reason as `work-files-changed` above;
      // the feed's "tasks due" card reads the same rows.
      queryClient.invalidateQueries({ queryKey: workTaskKeys.all });
      queryClient.invalidateQueries({ queryKey: workBoardKeys.all });
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      // FRONT-3227: the tabbed Inbox's To Do tab lists due tasks.
      queryClient.invalidateQueries({ queryKey: inboxKeys.todo() });
      break;
    case 'inbox-changed':
      // FRONT-3231: EDDY marked Inbox items done or moved them back from chat.
      // The same keys a Done mark / Move back from the page refreshes, for
      // every tab the agent can touch: the Done list, the markers New hides
      // by, New itself and To Do's unfinished chats.
      // FRONT-3339: EDDY also fires this after putting an item in the Inbox
      // (`inbox_create`), so the single-list Inbox's feed refreshes too.
      queryClient.invalidateQueries({ queryKey: inboxKeys.done() });
      queryClient.invalidateQueries({ queryKey: inboxKeys.markers() });
      // FRONT-3340: a Done mark takes an item out of Saved, and EDDY putting an
      // item (back) into New does too, so the Saved list is refreshed here.
      // (EDDY cannot save an item for later; only the person's own button does.)
      queryClient.invalidateQueries({ queryKey: inboxKeys.saved() });
      queryClient.invalidateQueries({ queryKey: [...inboxKeys.all, 'new'] });
      queryClient.invalidateQueries({ queryKey: inboxKeys.todo() });
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      break;
    case 'chat-scope-changed': {
      // FRONT-1813: `setSessionScope` (src/agents/chat-scope.ts) publishes
      // this on every genuine file/move/remove of a chat's Work-container
      // scope — the SAME chokepoint the "About: <name>" picker's own PUT
      // route and Eddy's own `work_chat_move` tool both write through.
      // FRONT-2563 adds a second publisher next to it,
      // `fileChatIntoProjectIfUnfiled`: a project made or fed from a chat
      // (plan commit, `work_project_create`) files that chat. Every
      // publisher is a deliberate, later user/model action
      // (`setSessionScopeIfUnset`, the opening-turn auto-scope path every
      // ordinary chat turn actually takes, publishes nothing) — so this is
      // never fired by ordinary chat activity, only by an actual scope change. The picker's own mutation already
      // updates its chat's cache directly via onSuccess, so the first
      // invalidation below is redundant (harmless) for that path — it's the
      // ONLY signal for the tool path, which has no mutation hook: without
      // it, an already-open chat window Eddy just filed mid-conversation
      // never learns its own scope changed until reload.
      //
      // FRONT-1813 finding 2: also invalidate `chatKeys.threadsRoot()` —
      // the SAME second invalidation `useSetChatScope`'s own `onSuccess`
      // fires (`use-chat-mutations.ts`), for the identical reason stated on
      // `chatKeys.byContainer`'s own docblock (`query-keys.ts`): a scope
      // change moves the chat in and out of `chatKeys.byContainer(...)` (a
      // project's Chats panel) and changes its row in the Chats-overview
      // list, and `byContainer` nests under the `threadsRoot()` prefix
      // specifically so one invalidation reaches both. Without this, a
      // tool-driven file/move/remove updates the "About: <name>" marker but
      // leaves the project's own Chats panel and the Chats-overview list
      // showing the pre-change state for up to their own staleTime.
      const scopedSessionId =
        typeof payload.externalId === 'string' ? payload.externalId : undefined;
      if (scopedSessionId) {
        queryClient.invalidateQueries({ queryKey: chatKeys.scope(scopedSessionId) });
        queryClient.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
      }
      break;
    }
    case 'operator-run-updated': {
      // FRONT-1989: `publishRunUpdated` (`src/operator/events.ts`) fires this
      // on the USER channel on every step/status change of a
      // `dispatch_desktop_task` run, carrying `{ runId, sessionId, status,
      // stepIndex }` — never read out of the payload here (this file's own
      // contract above); it exists only to know WHICH queries to
      // invalidate. `operatorKeys.run(runId)` refreshes the open card's own
      // polled detail immediately rather than waiting for its next
      // 3s-interval refetch. `operatorKeys.lookups()` (not a single
      // `forToolCall(...)` key) is invalidated broadly because this handler
      // has no toolCallId to key a narrower invalidation on — it only
      // matters for the brief window before a freshly-mounted card has
      // learned its own run id, and there is at most a couple of open
      // desktop-task cards on screen at once.
      const runId = typeof payload.runId === 'string' ? payload.runId : undefined;
      if (runId) {
        queryClient.invalidateQueries({ queryKey: operatorKeys.run(runId) });
      }
      queryClient.invalidateQueries({ queryKey: operatorKeys.lookups() });
      break;
    }
  }
}

export function useSSE(opts?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const sessionId = useSessionStore((s) => s.sessionId);
  const pushEvent = useEventStore((s) => s.pushEvent);
  // FRONT-2096: a session without an Eddy seat is refused the event stream
  // (403). EventSource hides the status, so the reconnect ladder below would
  // otherwise probe /me (200, they ARE signed in) and retry forever. The
  // shells pass `enabled: false` for such a session and nothing connects.
  const enabled = opts?.enabled ?? true;
  // Read at event time, so a flag that resolves after the stream opened still counts.
  const artifactWorkspace = useFeatureFlag(ARTIFACT_WORKSPACE_FLAG, false);
  const artifactWorkspaceRef = useRef(artifactWorkspace);
  artifactWorkspaceRef.current = artifactWorkspace;

  useEffect(() => {
    if (!enabled) return;
    // FRONT-3178: only the tab holding this session's lock runs `open`; every
    // tab, leader included, handles each event through `onMessage`.
    const open: OpenStream = (emit) => {
      let closed = false;
      let es: EventSource | null = null;
      let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
      // FRONT-157: consecutive-failure counter driving exponential backoff.
      // Reset on a successfully opened stream.
      let failures = 0;

      function connect() {
        if (closed) return;
        // `withCredentials: true` is REQUIRED so the browser attaches the
        // `eddy_session` auth cookie to the EventSource request. Without it the
        // stream reaches the backend with no cookie → 401 → the connection is
        // aborted and no live update ever arrives (only a manual reload, which
        // refetches via the normal authenticated fetches, would show results).
        const stream = new EventSource(`/api/events/${sessionId}`, {
          withCredentials: true,
        });
        es = stream;
        stream.onopen = () => {
          // Stream is live again — reset the backoff ladder.
          failures = 0;
        };
        for (const type of SSE_EVENT_TYPES) {
          stream.addEventListener(type, (e: MessageEvent<string>) => emit({ kind: 'event', type, data: e.data }));
        }
        stream.onerror = () => {
          if (closed) return;
          // EventSource does NOT auto-recover from a fatal error such as a
          // wrong-MIME response. On initial load the connection can briefly
          // get a `text/plain` response (a transient proxy hiccup during the
          // session-id handoff), which aborts the stream permanently — after
          // which no live update arrives until a manual page reload. Tear the
          // dead stream down and reconnect after a backoff so live updates
          // resume on their own.
          //
          // FRONT-157: the reconnect is auth-aware now. EventSource hides the
          // HTTP status, so before reconnecting we probe /api/auth/me: dead
          // session → one silent refresh attempt → still dead → signal the
          // auth gate and STOP. The old unconditional 1.5s loop hammered the
          // server with a 401 every 1.5s indefinitely once a session expired.
          stream.close();
          if (es === stream) es = null;
          failures += 1;
          // 1.5s → 3s → 6s → … capped at 30s, for transient/proxy errors.
          const delay = Math.min(1500 * 2 ** (failures - 1), 30_000);
          if (!reconnectTimer) {
            reconnectTimer = setTimeout(probeAuthThenReconnect, delay);
          }
        };
      }

      /**
       * True when the session is usable: /me answers non-401, or a silent
       * refresh rescued it. Network blips count as "valid" so the normal
       * reconnect retry (with backoff) handles them — and so does a
       * rate-limited/5xx refresh (FRONT-340): "transient" proves nothing
       * about the session, so we keep backing off instead of declaring
       * the user logged out.
       */
      async function sessionStillValid(): Promise<boolean> {
        try {
          const me = await fetch('/api/auth/me');
          if (me.status !== 401) return true;
          return (await tryRefreshToken()) !== 'expired';
        } catch {
          return true;
        }
      }

      /** Backoff timer body: verify auth, then reconnect — or hand off to the auth gate. */
      async function probeAuthThenReconnect(): Promise<void> {
        reconnectTimer = undefined;
        if (closed) return;
        if (!(await sessionStillValid())) {
          // Session definitively gone — let the auth gate route to login;
          // do not schedule another reconnect. Goes through the latched
          // signal (FRONT-340) so it can't re-fire a session-lost storm.
          // Emitted, so the follower tabs hear it too.
          emit({ kind: 'session-lost' });
          return;
        }
        if (!closed) connect();
      }

      connect();

      return () => {
        closed = true;
        if (reconnectTimer) clearTimeout(reconnectTimer);
        es?.close();
        es = null;
      };
    };

    return subscribeSharedStream(sessionId, open, (msg) => {
      if (msg.kind === 'session-lost') {
        signalSessionLost();
        return;
      }
      handleSSEEvent(msg.type, { data: msg.data } as MessageEvent, {
        queryClient,
        sessionId,
        pushEvent,
        artifactWorkspace: artifactWorkspaceRef.current,
      });
    });
  }, [sessionId, queryClient, pushEvent, enabled]);
}
